import { CarConfiMarketConfiguration } from '~/app/market-config/models/car-confi-market-configuration';
import { getModelIdString, getModelIdStringFromPr7Extension } from '~/graphql/mapper/model-id-string';
import {
	CarlineQuery,
	DriveTrainModelsFragment,
	IncentiveData,
	ModelIdentifier,
	Price,
	Status,
	TechnicalDataForModel,
} from '../__generated__/graphql';
import { FeatureCategory } from '../types';

export interface DriveTrain {
	type: 'driveTrain';
	__item: DriveTrainModelsFragment;
	modelIdStringPR3: string;
	modelIdString: string;
	incentives: IncentiveData[];
	featureId: string;
	carline?: string;
	pr7: string;
	debugCode: string;
	name: string;
	modelId: ModelIdentifier;
	status: Status;
	techData: TechnicalDataForModel | undefined | null;
	totalPrice: Price;
	monthlyRate: number | undefined | null;
	trimlineId: string;
	hidden: boolean;
}

export function mapCarline(response: CarlineQuery, config: CarConfiMarketConfiguration, debug = false) {
	const trimlinesFromCatalog = response.configuredCar?.catalog?.models?.data?.map((t) => ({
		...t,
		getModelIdString: getModelIdString(t?.identifier),
	}));

	const filteredDrivetrains = (
		trimline: NonNullable<CarlineQuery['configuredCar']['carlineStructureCarline']>['trimlines'][number],
	) => {
		return trimline?.models?.filter(({ hidden }) => debug || !hidden);
	};

	const multipleEngines = response.configuredCar?.carlineStructureCarline?.trimlines?.find(
		(trimline) => filteredDrivetrains(trimline)?.length > 1,
	);

	const carline = response.configuredCar.carlineStructureCarline?.name ?? 'no carline name provided';

	const getModelStatus = (modelId: ModelIdentifier): Status => {
		return (
			trimlinesFromCatalog?.find(
				(trimline) => trimline.getModelIdString === getModelIdStringFromPr7Extension(modelId),
			)?.status ?? defaultStatus
		);
	};

	const { useBasePriceWithFees } = config;

	const trimlines = response.configuredCar?.carlineStructureCarline?.trimlines
		.map((trimline) => {
			const cheapestModelPrice = () => {
				if (trimline.models) {
					const sortedByPrice = [...trimline.models].sort((a, b) => {
						const aPrice = a.prices.priceWithFees?.value || 0;
						const bPrice = b.prices.priceWithFees?.value || 0;
						return aPrice - bPrice;
					});
					return sortedByPrice[0].prices.priceWithFees!;
				}
				return undefined;
			};

			const cheapestBasePriceWithFees = () => {
				if (trimline.models) {
					const sortedByPrice = [...trimline.models].sort((a, b) => {
						const aPrice = a.basePriceWithFees?.value || 0;
						const bPrice = b.basePriceWithFees?.value || 0;
						return aPrice - bPrice;
					});
					return sortedByPrice[0].basePriceWithFees!;
				}
				return undefined;
			};

			const totalPrice = useBasePriceWithFees
				? cheapestBasePriceWithFees()
				: config.showPricesWithFees
					? cheapestModelPrice()
					: trimline.prices.minPrice;

			return {
				type: 'trimline' as const satisfies FeatureCategory,
				__item: trimline,
				featureId: trimline.id,
				name: multipleEngines ? trimline.name : filteredDrivetrains(trimline)[0]?.engineName, // if we show the combined card we want the cdb engine name
				model: trimlinesFromCatalog?.find((t) => trimline.id === t?.trimlineId)?.identifier,
				status:
					trimlinesFromCatalog?.find((t) => trimline.id === t?.trimlineId && t.status?.selected)?.status ??
					trimlinesFromCatalog?.find((t) => trimline.id === t?.trimlineId)?.status ??
					defaultStatus,
				totalPrice,
				pr7: trimline.id,
				monthlyRate: trimline.prices?.financing?.monthlyRate,
				driveTrains: filteredDrivetrains(trimline).map((model) =>
					mapDriveTrains(
						model,
						config.showPricesWithFees,
						carline,
						getModelStatus(model.modelId),
						useBasePriceWithFees,
						trimline.id,
					),
				),
			};
		})
		.filter(({ driveTrains }) => driveTrains.length > 0);

	return {
		carInfo: {
			carIdentifier: response.configuredCar.id,
			carline: response.configuredCar.carlineStructureCarline?.name,
			model: response.configuredCar.model?.name,
			incentives: response.configuredCar.incentives,
			technicalData: {
				consumption: 'Not available',
				emission: 'Not available',
			},
			// TODO: Find replacement for the summaries
			// technicalData: {
			// 	consumption:
			// 		response.configuredCar.technicalDataV2?.consumptionAndEmission?.summary?.consumptionSummary,
			// 	emission: response.configuredCar.technicalDataV2?.consumptionAndEmission?.summary?.emissionCO2Summary,
			// },
		},
		trimlines,
	};
}

const mapDriveTrains = (
	model: DriveTrainModelsFragment,
	showPricesWithFees: boolean,
	carline: string,
	status: Status,
	useBasePriceWithFees: boolean,
	trimlineId: string,
): DriveTrain => {
	const modelIdStringPR3 = getModelIdString(model.modelId);
	const modelIdString = getModelIdStringFromPr7Extension(model.modelId);
	const totalPriceDriveTrain = useBasePriceWithFees
		? model.basePriceWithFees
		: showPricesWithFees
			? model.prices.priceWithFees
			: model.prices.price;

	return {
		type: 'driveTrain' as const satisfies FeatureCategory,
		__item: model,
		modelIdStringPR3,
		modelIdString,
		incentives: (model.incentives || []).filter((incentive): incentive is IncentiveData => incentive != null),
		featureId: model.modelId.code,
		carline,
		pr7: model.modelId.code,
		debugCode: `${model.modelId.code}\\${model.modelId.version}${model.modelId.extensionsPR7?.length ? `\\${model.modelId.extensionsPR7.join('\\')}` : ''}\\${model.modelId.year}`,
		name: model.engineName,
		modelId: model.modelId,
		status,
		techData: model.technicalDataV2,
		totalPrice: totalPriceDriveTrain ?? { __typename: 'Price', value: -1 },
		monthlyRate: model.prices.financing?.monthlyRate,
		hidden: !!model.hidden,
		trimlineId,
	};
};

export type CarlineInfo = ReturnType<typeof mapCarline>;

/** Used as a fallback value to satisfy types. */
const defaultStatus: Status = {
	__typename: 'Status',
	available: true,
	inConflict: false,
	selected: false,
	changeable: true,
	standard: false,
};
